import type {
	BlockControlUIContext,
	BlockControlUIContextAncestor,
	BlockControlUIContextNode,
} from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { ActiveNode } from '../blockControlsPluginType';

const getNodeContext = (view: EditorView, pos: number): BlockControlUIContextNode | undefined => {
	const node = view.state.doc.nodeAt(pos);
	if (!node) {
		return undefined;
	}

	const $pos = view.state.doc.resolve(pos);
	const ancestors: BlockControlUIContextAncestor[] = [];
	for (let depth = 0; depth <= $pos.depth; depth++) {
		const ancestor = $pos.node(depth);
		ancestors.push({
			depth,
			pos: depth === 0 ? 0 : $pos.before(depth),
			type: { name: ancestor.type.name },
		});
	}

	return {
		ancestors,
		depth: $pos.depth + 1,
		node,
		parentType: $pos.parent.type.name,
		pos,
		type: { name: node.type.name },
	};
};

export const createBlockControlsSurfaceContext = (
	view: EditorView,
	activeNode: ActiveNode | undefined,
	target: 'active' | 'root' = 'root',
): BlockControlUIContext | undefined => {
	if (!activeNode) {
		return undefined;
	}

	const rootPos = activeNode.rootPos ?? activeNode.pos;
	const activeNodeContext = getNodeContext(view, activeNode.pos);
	const rootNodeContext = getNodeContext(view, rootPos);
	if (!activeNodeContext || !rootNodeContext) {
		return undefined;
	}

	return {
		activeNode: {
			...activeNodeContext,
			rootPos,
			rootType: { name: activeNode.rootNodeType ?? rootNodeContext.type.name },
		},
		rootNode: rootNodeContext,
		targetNode: target === 'active' ? activeNodeContext : rootNodeContext,
	};
};
